import { ComponentClass, createElement, forwardRef, FunctionComponent, HtmlHTMLAttributes, NamedExoticComponent, ReactNode } from "react";
import classNames from "classnames";
import { RefType } from "../../../types";
import { genericMemo } from "../../../utils";

import { ReactComponent as SelectedIcon } from "./icons/check.svg";

import styles from "./styles.module.scss";

export type BaseDropdownListItemProps<P extends object = HtmlHTMLAttributes<HTMLDivElement>, R extends unknown = HTMLDivElement> = {
  component?: string | FunctionComponent<P> | ComponentClass<P, any> | NamedExoticComponent<P>,
  ref?: RefType<R>,
  children?: ReactNode | ReactNode[],
  selected?: boolean
  focused?: boolean
  hovered?: boolean
  disabled?: boolean
  contentContainerProps?: HtmlHTMLAttributes<HTMLDivElement>
  contentWrapperProps?: HtmlHTMLAttributes<HTMLDivElement>
  selectedIconProps?: HtmlHTMLAttributes<HTMLSpanElement>
}

export type DropdownListItemProps<P extends object = HtmlHTMLAttributes<HTMLDivElement>, R extends unknown = HTMLDivElement> =
  BaseDropdownListItemProps<P, R> & Omit<P, keyof BaseDropdownListItemProps<P, R>>;

const DropdownListItemComponent = <P extends object = HtmlHTMLAttributes<HTMLDivElement>, R extends unknown = HTMLDivElement>(props: DropdownListItemProps<P, R>) => <></>;
type DropdownListItemComponentType = typeof DropdownListItemComponent;

export const DropdownListItem = genericMemo(forwardRef<HTMLDivElement, DropdownListItemProps>((
  {
    component = "div",
    children,
    className,
    selected,
    hovered,
    focused,
    disabled,
    contentContainerProps,
    contentWrapperProps,
    selectedIconProps,
    ...props
  },
  ref,
) => {
  return createElement(component as any, {
    ref,
    ...props,
    disabled,
    className: classNames(
      className,
      styles.root,
      {
        [styles.selected]: selected,
        [styles.hovered]: hovered,
        [styles.focused]: focused,
        [styles.disabled]: disabled,
      },
    ),
    children: (
      <div
        {...contentContainerProps}
        className={classNames(styles.content, contentContainerProps?.className)}
      >
        <div
          {...contentWrapperProps}
          className={classNames(styles.contentWrapper, contentWrapperProps?.className)}
        >
          {children}
        </div>
        {selected &&
          <span
            {...selectedIconProps}
            className={classNames(styles.icon, selectedIconProps?.className)}
          >
            <SelectedIcon />
          </span>
        }
      </div>
    ),
  });
}) as DropdownListItemComponentType);